import React, { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { 
  Calendar, 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  RefreshCw,
  BarChart3,
  PieChart
} from 'lucide-react'
import TransactionList from '../components/TransactionList'
import { useTransaction } from '../contexts/TransactionContext'
import { useWeb3Wallet } from '../contexts/Web3WalletProvider'

const HistoryPage: React.FC = () => {
  const { transactions } = useTransaction()
  const { isConnected } = useWeb3Wallet()
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')
  const [viewMode, setViewMode] = useState<'list' | 'analytics'>('list')

  // Filter transactions by time range
  const filteredTransactions = useMemo(() => {
    if (timeRange === 'all') return transactions
    
    const now = new Date()
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
    
    return transactions.filter(tx => new Date(tx.timestamp) >= cutoff)
  }, [transactions, timeRange])

  // Calculate statistics
  const stats = useMemo(() => {
    const sent = filteredTransactions.filter(tx => tx.type === 'send')
    const received = filteredTransactions.filter(tx => tx.type === 'receive')
    const pending = filteredTransactions.filter(tx => tx.status === 'pending')
    const confirmed = filteredTransactions.filter(tx => tx.status === 'completed')
    const failed = filteredTransactions.filter(tx => tx.status === 'failed')

    const totalSent = sent.reduce((sum, tx) => sum + parseFloat(tx.amount), 0)
    const totalReceived = received.reduce((sum, tx) => sum + parseFloat(tx.amount), 0)

    // Group by blockchain
    const byBlockchain = filteredTransactions.reduce((acc, tx) => {
      acc[tx.blockchain] = (acc[tx.blockchain] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    // Group by token
    const byToken = filteredTransactions.reduce((acc, tx) => {
      acc[tx.token] = (acc[tx.token] || 0) + parseFloat(tx.amount)
      return acc
    }, {} as Record<string, number>)

    return {
      total: filteredTransactions.length,
      sent: sent.length,
      received: received.length,
      pending: pending.length,
      confirmed: confirmed.length,
      failed: failed.length,
      totalSent,
      totalReceived,
      byBlockchain,
      byToken
    }
  }, [filteredTransactions])

  const timeRangeOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'all', label: 'All time' }
  ]

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center pb-24 md:pb-8">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="glass rounded-xl border border-white/20 p-8 max-w-md mx-auto">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-white/50" />
            <h2 className="text-2xl font-bold text-white mb-2">
              No Wallet Connected
            </h2>
            <p className="text-white/70">
              Connect your wallet to view transaction history
            </p>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Transaction History
              </h1>
              <p className="text-white/70">
                Track and analyze your payment activity
              </p>
            </div>
            
            <div className="flex items-center space-x-3 mt-4 md:mt-0">
              {/* View Mode Toggle */}
              <div className="flex items-center space-x-1 glass rounded-lg border border-white/20 p-1">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'list'
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  List
                </button>
                <button
                  onClick={() => setViewMode('analytics')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                    viewMode === 'analytics'
                      ? 'bg-white/20 text-white'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  Analytics
                </button>
              </div>

              {/* Time Range Selector */}
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value as any)}
                className="glass rounded-lg border border-white/20 bg-white/5 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              >
                {timeRangeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="glass rounded-xl border border-white/20 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              <span className="text-white/70 text-sm">Total</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.total}</div>
            <div className="text-white/50 text-xs">transactions</div>
          </div>

          <div className="glass rounded-xl border border-white/20 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="w-4 h-4 text-red-400" />
              <span className="text-white/70 text-sm">Sent</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.sent}</div>
            <div className="text-white/50 text-xs">
              {stats.totalSent.toFixed(4)} total
            </div>
          </div>

          <div className="glass rounded-xl border border-white/20 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <TrendingDown className="w-4 h-4 text-green-400" />
              <span className="text-white/70 text-sm">Received</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.received}</div>
            <div className="text-white/50 text-xs">
              {stats.totalReceived.toFixed(4)} total
            </div>
          </div>

          <div className="glass rounded-xl border border-white/20 p-4">
            <div className="flex items-center space-x-2 mb-2">
              <RefreshCw className="w-4 h-4 text-yellow-400" />
              <span className="text-white/70 text-sm">Pending</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.pending}</div>
            <div className="text-white/50 text-xs">
              {((stats.pending / stats.total) * 100 || 0).toFixed(1)}% of total
            </div>
          </div>
        </motion.div>

        {/* Content */}
        {viewMode === 'list' ? (
          /* Transaction List */
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="glass rounded-xl border border-white/20 p-6">
              <TransactionList />
            </div>
          </motion.div>
        ) : (
          /* Analytics View */
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Status Distribution */}
            <div className="glass rounded-xl border border-white/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <PieChart className="w-5 h-5" />
                <span>Transaction Status</span>
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-400 mb-1">
                    {stats.confirmed}
                  </div>
                  <div className="text-white/70 text-sm">Confirmed</div>
                  <div className="text-white/50 text-xs">
                    {((stats.confirmed / stats.total) * 100 || 0).toFixed(1)}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-400 mb-1">
                    {stats.pending}
                  </div>
                  <div className="text-white/70 text-sm">Pending</div>
                  <div className="text-white/50 text-xs">
                    {((stats.pending / stats.total) * 100 || 0).toFixed(1)}%
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-400 mb-1">
                    {stats.failed}
                  </div>
                  <div className="text-white/70 text-sm">Failed</div>
                  <div className="text-white/50 text-xs">
                    {((stats.failed / stats.total) * 100 || 0).toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Blockchain Distribution */}
            <div className="glass rounded-xl border border-white/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <BarChart3 className="w-5 h-5" />
                <span>By Blockchain</span>
              </h3>
              <div className="space-y-3">
                {Object.entries(stats.byBlockchain).map(([blockchain, count]) => (
                  <div key={blockchain} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        blockchain === 'ethereum' ? 'bg-blue-400' : 'bg-purple-400'
                      }`} />
                      <span className="text-white capitalize">{blockchain}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-white font-medium">{count}</span>
                      <span className="text-white/50 text-sm">
                        ({((count / stats.total) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Token Distribution */}
            <div className="glass rounded-xl border border-white/20 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
                <DollarSign className="w-5 h-5" />
                <span>By Token</span>
              </h3>
              <div className="space-y-3">
                {Object.entries(stats.byToken)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([token, amount]) => (
                  <div key={token} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {token.slice(0, 2)}
                        </span>
                      </div>
                      <span className="text-white">{token}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-medium">
                        {amount.toFixed(4)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Empty State */}
        {filteredTransactions.length === 0 && (
          <motion.div
            className="text-center py-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="glass rounded-xl border border-white/20 p-8 max-w-md mx-auto">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-white/50" />
              <h3 className="text-xl font-bold text-white mb-2">
                No Transactions Found
              </h3>
              <p className="text-white/70 mb-4">
                No transactions found for the selected time range
              </p>
              <button
                onClick={() => setTimeRange('all')}
                className="glass-button px-4 py-2 rounded-lg text-blue-400 hover:text-blue-300"
              >
                View All Time
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  )
}

export default HistoryPage